import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import QRCode from 'qrcode';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  color: #fff;
  animation: ${fadeInUp} 0.6s ease-out;
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 40px;
  max-width: 500px;
  width: 100%;
  text-align: center;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);

  @media (max-width: 600px) {
    padding: 30px 20px;
  }
`;

const Title = styled.h1`
  font-size: clamp(1.8rem, 5vw, 2.5rem);
  font-weight: 900;
  margin-bottom: 15px;
  background: linear-gradient(45deg, #f7931a, #ffeb3b);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Subtitle = styled.p`
  font-size: 1rem;
  color: #e0e0e0;
  margin-bottom: 30px;
  line-height: 1.5;
`;

const FeatureList = styled.div`
  text-align: left;
  margin: 30px 0;
  background: rgba(247, 147, 26, 0.1);
  border: 1px solid rgba(247, 147, 26, 0.3);
  border-radius: 12px;
  padding: 20px;
`;

const Feature = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
  font-size: 0.95rem;
  color: #e0e0e0;

  &:last-child {
    margin-bottom: 0;
  }

  span {
    color: #f7931a;
    font-size: 1.2rem;
  }
`;

const Button = styled.button`
  background: linear-gradient(45deg, #f7931a, #ffb74d);
  border: none;
  border-radius: 12px;
  padding: 16px 32px;
  font-size: 1rem;
  font-weight: 700;
  color: #000;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  margin-top: 20px;
  box-shadow: 0 4px 15px rgba(247, 147, 26, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(247, 147, 26, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const SecondaryButton = styled(Button)`
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  margin-top: 10px;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
`;

const QRContainer = styled.div`
  background: white;
  padding: 20px;
  border-radius: 12px;
  display: inline-block;
  margin: 20px 0;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
`;

const InvoiceBox = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 15px;
  margin: 15px 0;
  font-family: monospace;
  font-size: 12px;
  word-break: break-all;
  color: #000;
  max-height: 100px;
  overflow-y: auto;
  border: 1px solid #e9ecef;
`;

const StatusMessage = styled.div<{ type: 'success' | 'error' | 'info' }>`
  padding: 15px;
  border-radius: 8px;
  margin: 20px 0;
  font-weight: 600;
  background: ${props => 
    props.type === 'success' ? 'rgba(76, 175, 80, 0.2)' :
    props.type === 'error' ? 'rgba(244, 67, 54, 0.2)' :
    'rgba(33, 150, 243, 0.2)'
  };
  border: 1px solid ${props => 
    props.type === 'success' ? 'rgba(76, 175, 80, 0.5)' :
    props.type === 'error' ? 'rgba(244, 67, 54, 0.5)' :
    'rgba(33, 150, 243, 0.5)'
  };
  color: ${props => 
    props.type === 'success' ? '#81c784' :
    props.type === 'error' ? '#e57373' :
    '#64b5f6'
  };
  animation: ${pulse} 2s ease-in-out infinite;
`;

const PriceTag = styled.div`
  display: inline-block;
  background: linear-gradient(45deg, #f7931a, #ffb74d);
  color: #000;
  padding: 8px 20px;
  border-radius: 20px;
  font-size: 1.3rem;
  font-weight: 900;
  margin: 20px 0;
  box-shadow: 0 4px 10px rgba(247, 147, 26, 0.3);
`;

export const WrappedUpgrade: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const username = searchParams.get('user') || '';

  const [email, setEmail] = useState('');
  const [paymentData, setPaymentData] = useState<any>(null);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');
  const [isCreatingInvoice, setIsCreatingInvoice] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'paid' | 'failed'>('pending');
  const [statusMessage, setStatusMessage] = useState('');

  useEffect(() => {
    let pollInterval: NodeJS.Timeout;

    if (paymentData?.checking_id && paymentStatus === 'pending') {
      pollInterval = setInterval(async () => {
        try {
          const response = await fetch(`/api/payments/check-wrapped-history-payment/${paymentData.checking_id}`);
          const data = await response.json();

          if (data.paid) {
            setPaymentStatus('paid');
            setStatusMessage('Payment received! Processing your full history...');
            clearInterval(pollInterval);

            // Redirect after 3 seconds
            setTimeout(() => {
              navigate(`/wrapped?user=${username}`);
            }, 3000);
          }
        } catch (error) {
          console.error('Error checking payment:', error);
        }
      }, 3000); // Check every 3 seconds
    }

    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [paymentData, paymentStatus, username, navigate]);

  const createInvoice = async () => {
    if (!username) {
      setStatusMessage('Username is required');
      return;
    }

    setIsCreatingInvoice(true);
    try {
      const response = await fetch('/api/payments/create-wrapped-history-invoice', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: username,
          email: email || null,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create invoice');
      }

      const invoiceData = await response.json();

      // Generate QR code
      let qrDataUrl = '';
      if (invoiceData.invoice?.payment_request) {
        try {
          qrDataUrl = await QRCode.toDataURL(invoiceData.invoice.payment_request, {
            width: 256,
            margin: 2,
            color: {
              dark: '#000000',
              light: '#FFFFFF'
            }
          });
        } catch (error) {
          console.error('Error generating QR code:', error);
        }
      }

      setPaymentData(invoiceData.invoice);
      setQrCodeDataUrl(qrDataUrl);
      setStatusMessage('Scan QR code or copy invoice to pay with Lightning wallet');
    } catch (error: any) {
      console.error('Error creating invoice:', error);
      setStatusMessage(error.message || 'Failed to create payment invoice');
    } finally {
      setIsCreatingInvoice(false);
    }
  };

  const copyInvoice = async () => {
    if (!paymentData?.payment_request) return;

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(paymentData.payment_request);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = paymentData.payment_request;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setStatusMessage('Invoice copied to clipboard!');
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  return (
    <Container>
      <Card>
        <img 
          src="/POP_new_logopng.png" 
          alt="ProofOfPrice" 
          style={{ 
            width: '50px', 
            height: '50px', 
            marginBottom: '20px'
          }} 
        />

        <Title>⚡ Unlock Full History</Title>
        <Subtitle>Get complete analysis of @{username}'s Bitcoin journey</Subtitle>

        {!paymentData ? (
          <>
            <FeatureList>
              <Feature>
                <span>✅</span>
                <div><strong>Complete Tweet History</strong><br/>Download every single tweet, not just the recent 3,200</div>
              </Feature>
              <Feature>
                <span>✅</span>
                <div><strong>Full Prediction Analysis</strong><br/>We'll analyze ALL your tweets for Bitcoin price predictions</div>
              </Feature>
              <Feature>
                <span>✅</span>
                <div><strong>Your Own Account</strong><br/>Manage and revisit your historic tweets anytime</div>
              </Feature>
              <Feature>
                <span>✅</span>
                <div><strong>Chart Control</strong><br/>Decide what goes on the ProofOfPrice prediction chart</div>
              </Feature>
            </FeatureList>

            <PriceTag>⚡ 20,000 sats</PriceTag>

            <div style={{ marginTop: '20px', marginBottom: '10px' }}>
              <input
                type="email"
                placeholder="Email (optional, for updates)"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  background: 'rgba(255, 255, 255, 0.05)',
                  color: '#fff',
                  fontSize: '1rem',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            <Button onClick={createInvoice} disabled={isCreatingInvoice || !username}>
              {isCreatingInvoice ? '⚡ Creating Invoice...' : '⚡ Create Lightning Invoice'}
            </Button>

            <SecondaryButton onClick={() => navigate(`/wrapped?user=${username}`)}>
              ← Back to Wrapped
            </SecondaryButton>
          </>
        ) : (
          <>
            {paymentStatus === 'paid' ? (
              <StatusMessage type="success">
                ✅ {statusMessage}
              </StatusMessage>
            ) : (
              <>
                <StatusMessage type="info">
                  ⚡ {statusMessage}
                </StatusMessage>

                {qrCodeDataUrl && (
                  <QRContainer>
                    <img 
                      src={qrCodeDataUrl} 
                      alt="Lightning Payment QR Code"
                      style={{ maxWidth: '256px', width: '100%', display: 'block' }}
                    />
                  </QRContainer>
                )}

                <InvoiceBox>{paymentData.payment_request}</InvoiceBox>

                <Button onClick={copyInvoice}>
                  📋 Copy Lightning Invoice
                </Button>

                <SecondaryButton onClick={() => {
                  setPaymentData(null);
                  setQrCodeDataUrl('');
                  setPaymentStatus('pending');
                  setStatusMessage('');
                }}>
                  ← Create New Invoice
                </SecondaryButton>

                <div style={{ marginTop: '20px', fontSize: '0.85rem', color: '#aaa' }}>
                  Waiting for payment... This page will update automatically.
                </div>
              </>
            )}
          </>
        )}
      </Card>
    </Container>
  );
};
